import Phaser from 'phaser';

import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { BlackjackEvents, GameEvent, PileId, RoundState } from 'game-logic';
import { InterfaceEvents } from '../../services/game-services';
import { PlayerHandView } from './player-hand-view';
import { PlayerBettingBoxViewModel } from './player-betting-box-view-model';
import { ChipStackView } from '../chips/stack/chip-stack-view';
import { delay } from 'src/games/common/helper';
import { UxMetrics } from '../../model/ux-metrics';
import { RoundedNumberView } from '../components/rounded-number-view';

export class PlayerBettingBoxView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private graphics: Phaser.GameObjects.Graphics;
  private chipStackView: ChipStackView;
  private betAmountLabel: RoundedNumberView;
  private playerHandViews: Map<PileId, PlayerHandView> = new Map();
  private fadeValue = 0;

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: PlayerBettingBoxViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();

    this.graphics = scene.add.graphics();
    this.add(this.graphics);

    this.chipStackView = new ChipStackView(scene, 0, 0, viewModel.chipStackViewModel);
    this.add(this.chipStackView);

    this.betAmountLabel = new RoundedNumberView(scene, 0, 0, viewModel.betAmountViewModel);
    this.betAmountLabel.setVisible(false);
    this.add(this.betAmountLabel);

    viewModel.playerHandViewModels.forEach((viewModel) => {
      const playerHandView = new PlayerHandView(scene, 0, 0, viewModel);
      this.playerHandViews.set(playerHandView.playerId, playerHandView);
      this.add(playerHandView);
    });

    this.setName(PlayerBettingBoxView.name);
    scene.add.existing(this);

    this.setupProgress(0);

    this.subscriptionGroup.add(
      this.viewModel.playerHandChanges$.subscribe((change) => {
        if (change.type === 'add') {
          const playerHandView = new PlayerHandView(scene, 0, 0, change.viewModel);
          this.playerHandViews.set(playerHandView.playerId, playerHandView);
          this.add(playerHandView);
          if (this.currentResizeMetrics) {
            playerHandView.onResize(this.currentResizeMetrics);
          }
          this.layoutPlayerHands();
        } else if (change.type === 'remove') {
          const removeView = this.playerHandViews.get(change.viewModel.playerId);
          if (removeView) {
            removeView.destroy();
          }
          this.playerHandViews.delete(change.viewModel.playerId);
        }
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.onGameSetupStart$.subscribe(() => {
        this.scene.tweens.add({
          targets: this,
          fadeValue: 1,
          duration: UxMetrics.LoadingFade,
          ease: Phaser.Math.Easing.Quadratic.InOut,
          onUpdate: (_tween) => {
            this.setupProgress(this.fadeValue);
          },
        });
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.currentPlayerId$.subscribe((currentPlayerId) => {
        // Animate translation of "current" player to this player
        const playerOffsets = this.calculatePlayerHandOffset(currentPlayerId);

        this.playerHandViews.forEach((handView) => {
          const offsetX = playerOffsets[handView.playerId];

          this.scene.tweens.add({
            targets: [handView],
            x: offsetX,
            duration: UxMetrics.PlayerSwitchDelay,
            ease: Phaser.Math.Easing.Back.Out,
            onComplete: () => {
              // Maybe something
            },
          });
        });
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onResetGameScene$.subscribe(() => {
        this.betAmountLabel.setVisible(false);
        this.setupProgress(0);
      }),
    );
    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe(this.handleLogicEvents.bind(this));
      }),
    );
    this.subscriptionGroup.add(
      viewModel.interfaceEventQueue.subscribe(this.handleInterfaceEvents.bind(this)),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.chipStackView?.onResize(resizeMetrics);
    this.betAmountLabel?.onResize(resizeMetrics);
    this.playerHandViews.forEach((handView) => {
      handView.onResize(resizeMetrics);
    });

    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private setupProgress(progress: number): void {
    this.betAmountLabel.setAlpha(progress);
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    const boxScale = 0.8;
    const controlsWidth = layoutConfig.bettingBoxWidth * boxScale * scalingMetrics.scaleFactor;
    const controlsHeight = layoutConfig.bettingBoxHeight * boxScale * scalingMetrics.scaleFactor;

    const xPos = layoutConfig.boardWidth * 0.5;
    const yPos = layoutConfig.boardHeight * 0.65;

    this.setSize(controlsWidth, controlsHeight);
    this.setPosition(
      xPos * scalingMetrics.scaleFactor + scalingMetrics.scaledOffsetX,
      yPos * scalingMetrics.scaleFactor,
    );

    this.chipStackView.setPosition(0, 0);

    this.layoutPlayerHands();
    this.layoutBetAmount();
    this.drawGraphics();
  }

  private layoutPlayerHands(): void {
    const playerOffsets = this.calculatePlayerHandOffset(this.viewModel.playerIdNow);

    this.playerHandViews.forEach((handView) => {
      const offsetX = playerOffsets[handView.playerId];
      handView.setPosition(offsetX, 0);
    });
  }

  private calculatePlayerHandOffset(currentPlayerId: PileId): Record<PileId, number> {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1.0;
    const spacing = this.viewModel.layoutConfig.boardWidth * 0.35 * scaleFactor;

    const currentIndex = Array.from(this.playerHandViews.keys()).indexOf(currentPlayerId);

    // Assign offset so that the current player is at (0, 0)
    const positions: Record<PileId, number> = {} as Record<PileId, number>;
    Array.from(this.playerHandViews.values()).forEach((view, index) => {
      positions[view.playerId] = (index - currentIndex) * spacing;
    });

    return positions;
  }

  private layoutBetAmount(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    const boxScale = 0.8;
    const controlsWidth = layoutConfig.bettingBoxWidth * boxScale * scalingMetrics.scaleFactor;

    const padding = layoutConfig.paddingLg * scalingMetrics.scaleFactor;
    this.betAmountLabel.setPosition(
      controlsWidth * 0.5 + this.betAmountLabel.displayWidth * 0.5 + padding,
      0,
    );
  }

  private drawGraphics(): void {
    this.graphics.clear(); // Clear previous drawings

    this.graphics.lineStyle(3, 0xffffff, 0.3);
    this.graphics.strokeRoundedRect(
      this.width * -0.5,
      this.height * -0.5,
      this.width,
      this.height,
      this.width * 0.05,
    );
  }

  private async handleLogicEvents(event: GameEvent<BlackjackEvents>): Promise<void> {
    // Don't process instant events
    if (event.instant) {
      return;
    }

    for (const playerHandView of this.playerHandViews.values()) {
      await playerHandView.handleLogicEvents(event);
    }

    switch (event.type) {
      case 'onRoundStateChanged':
        this.betAmountLabel.setVisible(event.payload.state === RoundState.Deal);
        break;
      case 'onMoveToNextRound':
        this.viewModel.clearPlayerChips();
        break;
      case 'onPlayerBet':
        this.viewModel.chipStackCleared();
        break;
      case 'onSplitUpdatePlayerScore':
        await this.viewModel.splitPlayer(
          event.payload.player,
          event.payload.nextPlayer,
          event.payload.movedCardIndex,
        );
        break;
      case 'onMoveNextPlayer':
        await this.viewModel.moveNextPlayer(event.payload.player);
        break;
      case 'onCurrentPlayerChanged':
        await this.viewModel.moveNextPlayer(event.payload.player);
        break;
      case 'onPlayerCleared':
        this.viewModel.clearPlayer(event.payload.player);
        break;
      default:
        break;
    }
  }

  private async handleInterfaceEvents(event: GameEvent<InterfaceEvents>): Promise<void> {
    switch (event.type) {
      case 'onBetCleared':
        this.viewModel.chipStackCleared();
        this.viewModel.setBetStackAmount(event.payload.totalBetAmount);
        this.layoutBetAmount();
        break;
      case 'onBetIncreased':
        this.viewModel.chipStackIncreased(event.payload.chip);
        this.viewModel.setBetStackAmount(event.payload.totalBetAmount);
        this.layoutBetAmount();
        break;
      case 'onBetAllIn':
        {
          this.viewModel.chipStackCleared();

          const totalChips = event.payload.chips.length;
          const defaultDelay = 50;
          const minDelay = 10;
          const maxTotalTime = 300;

          const delayPerChip = Math.min(
            defaultDelay,
            Math.max(minDelay, maxTotalTime / totalChips),
          );

          for (const chip of event.payload.chips) {
            this.viewModel.chipStackIncreased(chip);
            await delay(delayPerChip);
          }

          this.viewModel.setBetStackAmount(event.payload.totalBetAmount);
          this.layoutBetAmount();
        }
        break;
      default:
        break;
    }
  }
}
